import React, {
    useEffect,
    useState,
    MutableRefObject,
} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {FormControlLabel, Switch, Button} from '@material-ui/core';
import {AspectRatioType} from './SnapshotContainer';
import {ApSelectionTool} from '@accurate-player/accurate-player-components-external';
import {CoordinatesState} from './CreateArtworkDialog';
// @ts-ignore
import safeGuardImage from './assets/safe-zone-16-9.png';

export type ArtworkRefType = MutableRefObject<ApSelectionTool>;

export interface SpecificAspectRatioArtworkProps {
    currentAspectRatio: AspectRatioType;
    currentArtwork: string;
    setPreviewImageAspectRatio?: (value: string) => void;
    setCoordinatesState?: (value: CoordinatesState) => void;
}

export const resetArtworkSelection = wrap((artworkRef: ArtworkRefType) => {
    const artworkApi = artworkRef.current.api;

    artworkApi.resetSelectionArea();
});

export const changeSwitch = curry((
    artworkRef: ArtworkRefType,
    attrName: string,
    setSwitchValue: (val: boolean) => void,
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
) => {
    const artwork = artworkRef.current;

    (artwork as any)[attrName] = checked

    setSwitchValue(checked);
});

export const changeSwitchSafeGuard = curry((
    setOverlayImage: (value: string) => void,
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
) => {
    if (checked) {
        setOverlayImage(safeGuardImage);
    } else {
        setOverlayImage('')
    }
});

export const changeSnapToValue = curry((
    artworkRef: ArtworkRefType,
    setSwitchValue: (val: boolean) => void,
    snapOption: string,
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
) => {
    const artwork = artworkRef.current;

    (artwork.snapOptions as any)[snapOption] = checked;

    setSwitchValue(checked);
});

export const changeSelect = curry((setSelectValue: (value: string) => void, e: any) => {
    setSelectValue(e.target.value);
});

export const artworkApRelease = curry((
    setPreviewImageAspectRatio: (value: string) => void,
    artworkRef: ArtworkRefType,
    setCoordinatesState: (value: CoordinatesState) => void,
    e: any,
) => {
    const {left, bottom, right, top, width, height} = e.relativeRect;
    const coordinatesState = {
        left,
        bottom,
        right,
        top,
        width,
        height,
    };

    setCoordinatesState(coordinatesState);

    const artworkApi = artworkRef.current.api;
    const previewImageUrl = artworkApi.extractImageUrl('webp');

    setPreviewImageAspectRatio(previewImageUrl);
});

export const artworkApInit = curry((
    setCoordinatesState: (value: CoordinatesState) => void,
    e: any,
) => {
    const {left, top, right, bottom, width, height} = e.relativeRect;
    const coordinatesState = {
        left,
        bottom,
        right,
        top,
        width,
        height,
    };

    if (setCoordinatesState) {
        setCoordinatesState(coordinatesState);
    }
});

export const setInitialSelectionToolsOptions = wrap((
    artworkRef: ArtworkRefType,
    setPreviewImageAspectRatio: () => void,
    setCoordinatesState: (value: CoordinatesState) => void,
) => {
    const artwork = artworkRef?.current;

    if (artwork) {
        artwork.apRelease = artworkApRelease(setPreviewImageAspectRatio, artworkRef, setCoordinatesState);
        artwork.apInit = artworkApInit(setCoordinatesState);
        artwork.showBounds = true;
        artwork.highlightSides = true;

        if (artwork.snapOptions) {
            artwork.snapOptions.snapToCenter = true;
            artwork.snapOptions.snapToSides = true;
        }
    }
});

const SpecificAspectRatioArtwork = React.forwardRef((props: SpecificAspectRatioArtworkProps, ref: any) => {
    const {
        currentAspectRatio,
        currentArtwork,
        setPreviewImageAspectRatio,
        setCoordinatesState,
    } = props;
    const [restrictBlackBorders, setRestrictBlackBorders] = useState<boolean>( false);
    const [highlightEdges, setHighlightEdges] = useState<boolean>(true);
    const [snapToCenter, setSnapToCenter] = useState<boolean>(true);
    const [snapToEdges, setSnapToEdges] = useState<boolean>(true);
    const [showBounds, setShowBounds] = useState<boolean>(true);
    const [overlayImage, setOverlayImage] = useState<string>('');

    useEffect(setInitialSelectionToolsOptions(
        ref,
        setPreviewImageAspectRatio,
        setCoordinatesState,
    ), [])

    return (
        <>
            <div className="create-artwork-dialog__body__sidebar-settings">
                <div className="create-artwork-dialog__header">
                    Toolbox
                </div>
                <div className="setting-option setting-switch setting-switch_snap-to-center">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={snapToCenter}
                                onChange={changeSnapToValue(ref, setSnapToCenter, 'snapToCenter')}
                                name="Snap to Center"
                                color="primary"
                                size="small"
                            />
                        }
                        label="Snap to Center"
                        labelPlacement="start"
                    />
                </div>
                <div className="setting-option setting-switch setting-switch_snap-to-edges">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={snapToEdges}
                                onChange={changeSnapToValue(ref, setSnapToEdges, 'snapToSides')}
                                name="Snap to Edges"
                                color="primary"
                                size="small"
                            />
                        }
                        label="Snap to Edges"
                        labelPlacement="start"
                    />
                </div>
                <div className="setting-option setting-switch setting-switch_restrict-black-border">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={restrictBlackBorders}
                                onChange={changeSwitch(ref, 'suppressBlackBorders', setRestrictBlackBorders)}
                                name="Restrict black borders"
                                color="primary"
                                size="small"
                            />
                        }
                        label="Restrict Black Bars"
                        labelPlacement="start"
                    />
                </div>
                <div className="setting-option setting-switch setting-switch_highlight-edges">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={highlightEdges}
                                onChange={changeSwitch(ref, 'highlightSides', setHighlightEdges)}
                                name="Highlight Edges"
                                color="primary"
                                size="small"
                            />
                         }
                        label="Highlight Edges"
                        labelPlacement="start"
                    />
                </div>
                {currentAspectRatio === '16:9' && (
                    <div className="setting-option setting-switch setting-switch_safe-zone">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(overlayImage)}
                                    onChange={changeSwitchSafeGuard(setOverlayImage)}
                                    name="Safe Zone Guide"
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="Safe Zone Guide"
                            labelPlacement="start"
                        />
                    </div>
                )}
                <div className="setting-option setting-switch setting-switch_show-bounds">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showBounds}
                                onChange={changeSwitch(ref, 'showBounds', setShowBounds)}
                                name="Show Bounds"
                                color="primary"
                                size="small"
                            />
                        }
                        label="Show Bounds"
                        labelPlacement="start"
                    />
                </div>
                <Button
                    className="reset-selection-button"
                    onClick={resetArtworkSelection(ref)}
                    variant='outlined'
                >
                    RESET SELECTION
                </Button>
            </div>
            <div className="create-artwork-dialog__body__artwork-image">
                <ap-selection-tool
                    ref={ref}
                    class="artwork-selection-tool"
                    force-aspect-ratio={true}
                    aspect-ratio={currentAspectRatio}
                    guideline-layout={1}
                    overlay-image-source={overlayImage}
                >
                    <img
                        crossOrigin="anonymous"
                        src={currentArtwork}
                        alt="image"
                    />
                </ap-selection-tool>
            </div>
        </>
    );
});

export default SpecificAspectRatioArtwork;
